import { css } from "@emotion/react";
import { FaCheckSquare, FaRegCheckSquare, FaRegSquare } from "react-icons/fa";
import { useAccountID } from "../store/account";
import { usePlans } from "../store/plans";
import growImage from "../../assets/grow.png";
import { Helmet } from "react-helmet";

export default function Upgrade({ standAlone = true }) {
    return (
        <>
            {standAlone ? (
                <>
                    <Helmet>
                        <title>Grow Your Show - Podtrac</title>
                    </Helmet>
                    <h1>Grow Your Show</h1>
                    <p>
                        The Podtrac Grow Your Show plan is the easiest way to access exactly the
                        metrics and guidance you need to truly understand and monitor your show
                        and/or network performance over time and gain the insights you need to take
                        action to grow.
                    </p>
                    <p>
                        <UpgradeButton /> for $20 per month{" "}
                        <span style={{ fontSize: "0.75em" }}>(billed annually)</span>
                    </p>
                    <img style={{ width: 200, float: "right" }} src={growImage} />
                    <p>
                        The Grow Your Show plan <b>is for you</b> if:
                    </p>
                    <ul
                        css={css`
                            & li {
                                margin: 0.25rem 0;
                            }
                        `}
                    >
                        <li>You’re ready to get serious about growing your podcast audience</li>
                        <li>
                            You’re tired of trying to piece together and understand metrics from
                            multiple platforms and just want to see the metrics that matter to your
                            growth in the way that makes sense
                        </li>
                        <li>
                            You are ready to look past the surface and vanity metrics and dig into
                            the metrics to truly understand your audience
                        </li>
                        <li>
                            You have a network of podcasts and want to provide account access to
                            multiple users
                        </li>
                    </ul>
                </>
            ) : null}
            <p>
                The Grow Your Show plan includes these <b>powerful features</b> for all shows in
                your account:
            </p>
            <ul
                css={css`
                    & li {
                        margin: 0.25rem 0;
                    }
                `}
            >
                <li>
                    <b>Unlimited Data History</b> - access all data from the point in time when you
                    began to measure with Podtrac in order to identify important trends
                </li>
                <li>
                    <b>Smart Excel Exports</b> - download to Excel the data behind each Podtrac
                    report on your dashboard in order to share, archive and further analyze your
                    data
                </li>
                <li>
                    <b>Episode Comparison Report</b> - easily compare the performance of each of
                    your episodes to determine which perform the best
                </li>
                <li>
                    <b>Show Rankings</b> - the Rankings tabs on your dashboard provide insight into
                    how your shows rank against all Podtrac measured shows
                </li>
                <li>
                    <b>Multi-User access with User Permissions</b> - provide your team members
                    access to your Podtrac dashboard with the ability to limit individuals to seeing
                    just the data for specific shows
                </li>
                <li>
                    <b>Office Hour Support</b> - get your questions answered by a Podtrac pro during
                    our weekly scheduled Zoom Office Hours.
                </li>
            </ul>
            <table
                css={css`
                    border-collapse: collapse;
                    margin: 1rem 0;
                    & td,
                    & th {
                        border: 1px solid #eee;
                        padding: 0.5rem;
                    }
                    & .heading {
                        background: #d8e9f0;
                        border: 1px solid #d8e9f0;
                        font-weight: 400;
                    }
                `}
            >
                <thead>
                    <tr>
                        <th rowSpan={2} style={{ textAlign: "left" }}>
                            <h2>Compare Plans</h2>
                        </th>
                        <th style={{ width: "30%" }}>
                            Getting Started
                            <br />
                            <br />
                            <div style={{ fontWeight: 300 }}>FREE</div>
                        </th>
                        <th style={{ width: "30%" }}>
                            Grow Your Show
                            <div style={{ fontWeight: 300 }}>
                                $20 per month
                                <br />
                                <span style={{ fontSize: "0.75em" }}>(billed annually)</span>
                            </div>
                        </th>
                    </tr>
                    {standAlone ? (
                        <tr>
                            <td style={{ color: "#999", fontSize: "0.75rem", textAlign: "center" }}>
                                <i>current plan</i>
                            </td>
                            <td style={{ textAlign: "center" }}>
                                <UpgradeButton />
                            </td>
                        </tr>
                    ) : null}
                </thead>
                <tbody>
                    <tr>
                        <th colSpan={3} style={{ textAlign: "left" }} className="heading">
                            Account Features/Functionality
                        </th>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Shows</th>
                        <td style={{ textAlign: "center" }}>Unlimited</td>
                        <td style={{ textAlign: "center" }}>Unlimited</td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Downloads per month</th>
                        <td style={{ textAlign: "center" }}>Unlimited</td>
                        <td style={{ textAlign: "center" }}>Unlimited</td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Data History</th>
                        <td style={{ textAlign: "center" }}>90 days of data</td>
                        <td style={{ textAlign: "center" }}>
                            <b>Unlimited</b>
                        </td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Update Frequency</th>
                        <td style={{ textAlign: "center" }}>Daily</td>
                        <td style={{ textAlign: "center" }}>Daily</td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}># of Users</th>
                        <td style={{ textAlign: "center" }}>1 user</td>
                        <td style={{ textAlign: "center" }}>
                            <b>5 users</b>
                        </td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>User Permissions</th>
                        <td style={{ textAlign: "center" }}>-</td>
                        <td style={{ textAlign: "center" }}>
                            <b>Limit access to specific shows</b>
                        </td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Printable Reports</th>
                        <td style={{ textAlign: "center" }}>All included reports</td>
                        <td style={{ textAlign: "center" }}>All included reports</td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Data Export</th>
                        <td style={{ textAlign: "center" }}>-</td>
                        <td style={{ textAlign: "center" }}>
                            <b>Excel export of report data</b>
                        </td>
                    </tr>
                    <tr>
                        <th colSpan={3} style={{ textAlign: "left" }} className="heading">
                            Data/Report Features
                        </th>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>IAB-certified analytics</th>
                        <td style={{ textAlign: "center" }}>
                            <FaRegCheckSquare />
                        </td>
                        <td style={{ textAlign: "center" }}>
                            <FaRegCheckSquare />
                        </td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Demographic Survey</th>
                        <td style={{ textAlign: "center" }}>
                            <FaRegCheckSquare />
                        </td>
                        <td style={{ textAlign: "center" }}>
                            <FaRegCheckSquare />
                        </td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Key Metrics Report</th>
                        <td style={{ textAlign: "center" }}>
                            <FaRegCheckSquare />
                        </td>
                        <td style={{ textAlign: "center" }}>
                            <FaRegCheckSquare />
                        </td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Show Overview Report</th>
                        <td style={{ textAlign: "center" }}>
                            <FaRegCheckSquare />
                        </td>
                        <td style={{ textAlign: "center" }}>
                            <FaRegCheckSquare />
                        </td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Episode Detail</th>
                        <td style={{ textAlign: "center" }}>
                            <FaRegCheckSquare />
                        </td>
                        <td style={{ textAlign: "center" }}>
                            <FaRegCheckSquare />
                        </td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Episode Comparison Report</th>
                        <td style={{ textAlign: "center" }}>
                            <FaRegSquare />
                        </td>
                        <td style={{ textAlign: "center" }}>
                            <FaCheckSquare />
                        </td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Show Rankings</th>
                        <td style={{ textAlign: "center" }}>
                            <FaRegSquare />
                        </td>
                        <td style={{ textAlign: "center" }}>
                            <FaCheckSquare />
                        </td>
                    </tr>
                    <tr>
                        <th colSpan={3} style={{ textAlign: "left" }} className="heading">
                            Support
                        </th>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Implementation and User Support</th>
                        <td style={{ textAlign: "center" }}>Email</td>
                        <td style={{ textAlign: "center" }}>
                            <b>Email/Zoom Office Hours</b>
                        </td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: "left" }}>Educational Support</th>
                        <td style={{ textAlign: "center" }}>Knowledgebase and emails</td>
                        <td style={{ textAlign: "center" }}>Knowledgebase and emails</td>
                    </tr>

                    {standAlone ? (
                        <tr>
                            <th style={{ textAlign: "left" }} />
                            <td style={{ color: "#999", fontSize: "0.75rem", textAlign: "center" }}>
                                <i>current plan</i>
                            </td>
                            <td style={{ textAlign: "center" }}>
                                <UpgradeButton />
                            </td>
                        </tr>
                    ) : null}
                </tbody>
            </table>
        </>
    );
}

function UpgradeButton() {
    const accountID = useAccountID();
    const plan = usePlans().data?.find(plan => plan.prices[0].amount > 0);
    const price = plan?.prices[0];
    const beginCheckout = () => {
        window.location = `/${accountID}/checkout/start/${price.productPriceID}`;
    };
    return (
        <button
            css={css`
                background: rgba(255, 183, 51, 1);
                background: linear-gradient(
                    180deg,
                    rgba(255, 212, 92, 1) 0%,
                    rgba(255, 183, 51, 1) 100%
                );
                border: 0.125rem solid #f0592b;
                border-radius: 2rem;
                cursor: pointer;
                padding: 0.25rem 1rem;
                font-size: 1rem;
                font-weight: 700;
                color: #7b4400;
                line-height: 1.5rem;

                &:hover {
                    background: #f8b12f;
                    border-color: #b74928;
                }
            `}
            onClick={beginCheckout}
        >
            Upgrade Now
        </button>
    );
}
